import { useQuery } from '@tanstack/react-query';
import { Link, useLocation } from 'react-router-dom';
import { dutyApi } from '../api';
import { t } from '../i18n';
import { cx } from '../lib/cx';
import { hasRole, useAuth } from '../stores/auth';

/**
 * true, коли зараз ніхто не чергує (лише для owner/manager). Дзвіночок викликає хук одразу,
 * щоб попередження вже було готове в момент відкриття панелі, а не зʼявлялося із запізненням.
 */
export function useDutyMissing(): boolean {
  const me = useAuth((s) => s.me);
  const allowed = hasRole(me, 'owner', 'manager');
  const current = useQuery({
    queryKey: ['duty-current'],
    queryFn: dutyApi.current,
    enabled: allowed,
    refetchInterval: 60_000,
    retry: false,
  });
  return allowed && !!current.data && current.data.length === 0;
}

/**
 * «Немає чергового»: зараз ніхто не на чергуванні, тож нові заявки падають на власника.
 * Лише для owner/manager — вони ставлять чергування. compact — один рядок (у дзвіночку).
 */
export function DutyWarning({ compact = false, className }: { compact?: boolean; className?: string }) {
  const missing = useDutyMissing();
  const { pathname, search } = useLocation();
  if (!missing) return null;
  // уже на вкладці «Чергування» посилання вело б на ту саму сторінку — підказуємо, яку кнопку натиснути
  const here = pathname === '/planner' && new URLSearchParams(search).get('tab') === 'duty';
  const link = here ? (
    <span className="text-sm text-lab">{t('duty.noneHere')}</span>
  ) : (
    <Link to="/planner?tab=duty" className="link text-sm whitespace-nowrap">
      {t('duty.noneLink')}
    </Link>
  );
  if (compact) {
    return (
      <div role="status" className={cx('flex items-center justify-between gap-3 px-3 py-2 rounded-lg bg-warn-soft text-sm', className)}>
        <span className="font-semibold truncate" title={t('duty.none')}>
          {t('duty.noneShort')}
        </span>
        {link}
      </div>
    );
  }
  return (
    <div role="status" className={cx('flex flex-wrap items-center justify-between gap-x-4 gap-y-2 px-4 py-3 rounded-lg bg-warn-soft mb-4', className)}>
      <span className="font-semibold">{t('duty.none')}</span>
      {link}
    </div>
  );
}
